import { DatePipe, NgComponentOutlet } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { PERMISSION_ACTION } from '@mb/auth/shared';
import { SCOPED_ACCESS_GATE } from '@mb/auth/client';
import type { Action } from '@mb/components';
import { DetailsShellComponent, TableStateService } from '@mb/components';
import { HlmTabsImports } from '@mb/ui/tabs';
import {
  PERSON_SCOPED_ACTION,
  PERSON_SCOPED_ENTITY,
  type IPerson,
} from '../../shared';
import {
  createDeletePersonAction,
  createResendPersonInviteAction,
} from './person-admin.actions';
import { PERSON_ADMIN_FLOW } from './person-admin.flow';
import { PERSON_DETAIL_TABS } from './person-detail-tabs';

@Component({
  selector: 'workforce-person-details',
  standalone: true,
  host: { class: 'contents' },
  imports: [
    DetailsShellComponent,
    TranslocoPipe,
    DatePipe,
    HlmTabsImports,
    NgComponentOutlet,
  ],
  template: `
    @if (person(); as currentPerson) {
      <app-details-shell
        [portrait]="true"
        [title]="fullName()"
        [subtitle]="subtitle()"
        [actions]="visibleActions()"
      >
        <hlm-tabs tab="details">
          <hlm-tabs-list variant="line" class="w-full">
            <button hlmTabsTrigger="details">
              {{ 'persons.list.tabs.details' | transloco }}
            </button>
            @for (tab of extraTabs; track tab.id) {
              <button [hlmTabsTrigger]="tab.id">
                {{ tab.labelKey | transloco }}
              </button>
            }
            <button hlmTabsTrigger="settings">
              {{ 'persons.list.tabs.settings' | transloco }}
            </button>
          </hlm-tabs-list>

          <div hlmTabsContent="details" class="space-y-6 pt-4">
            <section class="space-y-3">
              <dl
                class="overflow-hidden rounded-xl border border-border bg-background"
              >
                <div
                  class="grid gap-2 border-b border-border px-4 py-3 text-sm sm:grid-cols-2"
                >
                  <dt class="text-muted-foreground">
                    {{ 'persons.list.fields.mobile' | transloco }}
                  </dt>
                  <dd class="text-foreground sm:text-right">
                    {{ displayValue(currentPerson.phone) }}
                  </dd>
                </div>
                <div class="grid gap-2 px-4 py-3 text-sm sm:grid-cols-2">
                  <dt class="text-muted-foreground">
                    {{ 'persons.list.fields.privateMobile' | transloco }}
                  </dt>
                  <dd class="text-foreground sm:text-right">
                    {{ displayValue(currentPerson.privateMobile) }}
                  </dd>
                </div>
              </dl>
            </section>

            <section class="space-y-3">
              <h3 class="text-base font-semibold text-foreground">
                {{ 'persons.list.details.sections.address' | transloco }}
              </h3>
              <dl
                class="overflow-hidden rounded-xl border border-border bg-background"
              >
                <div
                  class="grid gap-2 border-b border-border px-4 py-3 text-sm sm:grid-cols-2"
                >
                  <dt class="text-muted-foreground">
                    {{ 'persons.list.fields.street' | transloco }}
                  </dt>
                  <dd class="text-foreground sm:text-right">
                    {{ displayValue(currentPerson.street) }}
                  </dd>
                </div>
                <div
                  class="grid gap-2 border-b border-border px-4 py-3 text-sm sm:grid-cols-2"
                >
                  <dt class="text-muted-foreground">
                    {{ 'persons.list.fields.city' | transloco }}
                  </dt>
                  <dd class="text-foreground sm:text-right">
                    {{ displayValue(currentPerson.city) }}
                  </dd>
                </div>
                <div
                  class="grid gap-2 border-b border-border px-4 py-3 text-sm sm:grid-cols-2"
                >
                  <dt class="text-muted-foreground">
                    {{ 'persons.list.fields.postalCode' | transloco }}
                  </dt>
                  <dd class="text-foreground sm:text-right">
                    {{ displayValue(currentPerson.postalCode) }}
                  </dd>
                </div>
                <div class="grid gap-2 px-4 py-3 text-sm sm:grid-cols-2">
                  <dt class="text-muted-foreground">
                    {{ 'persons.list.fields.country' | transloco }}
                  </dt>
                  <dd class="text-foreground sm:text-right">
                    {{ displayValue(currentPerson.country) }}
                  </dd>
                </div>
              </dl>
            </section>

            <section class="space-y-3">
              <h3 class="text-base font-semibold text-foreground">
                {{ 'persons.list.details.sections.email' | transloco }}
              </h3>
              <dl
                class="overflow-hidden rounded-xl border border-border bg-background"
              >
                <div
                  class="grid gap-2 border-b border-border px-4 py-3 text-sm sm:grid-cols-2"
                >
                  <dt class="text-muted-foreground">
                    {{ 'contacts.form.fields.email' | transloco }}
                  </dt>
                  <dd class="text-foreground sm:text-right">
                    {{ displayValue(currentPerson.email) }}
                  </dd>
                </div>
                <div class="grid gap-2 px-4 py-3 text-sm sm:grid-cols-2">
                  <dt class="text-muted-foreground">
                    {{ 'persons.list.fields.privateEmail' | transloco }}
                  </dt>
                  <dd class="text-foreground sm:text-right">
                    {{ displayValue(currentPerson.additionalEmail) }}
                  </dd>
                </div>
              </dl>
            </section>

            <section class="space-y-3">
              <h3 class="text-base font-semibold text-foreground">
                {{ 'persons.list.details.sections.administration' | transloco }}
              </h3>
              <dl
                class="overflow-hidden rounded-xl border border-border bg-background"
              >
                <div
                  class="grid gap-2 border-b border-border px-4 py-3 text-sm sm:grid-cols-2"
                >
                  <dt class="text-muted-foreground">
                    {{ 'persons.list.fields.socialSecurityNumber' | transloco }}
                  </dt>
                  <dd class="text-foreground sm:text-right">
                    {{ displayValue(currentPerson.socialSecurityNumber) }}
                  </dd>
                </div>
                <div
                  class="grid gap-2 border-b border-border px-4 py-3 text-sm sm:grid-cols-2"
                >
                  <dt class="text-muted-foreground">
                    {{ 'persons.list.fields.personnelNumber' | transloco }}
                  </dt>
                  <dd class="text-foreground sm:text-right">
                    {{ displayValue(currentPerson.personnelNumber) }}
                  </dd>
                </div>
                <div class="grid gap-2 px-4 py-3 text-sm sm:grid-cols-2">
                  <dt class="text-muted-foreground">
                    {{ 'persons.list.fields.vacationDays' | transloco }}
                  </dt>
                  <dd class="text-foreground sm:text-right">
                    {{ displayValue(currentPerson.vacationDays) }}
                  </dd>
                </div>
              </dl>
            </section>

            <section class="space-y-3">
              <h3 class="text-base font-semibold text-foreground">
                {{
                  'persons.list.details.sections.socialSecurityReports'
                    | transloco
                }}
              </h3>
              <p class="text-sm text-muted-foreground">
                {{
                  'persons.list.details.sections.socialSecurityReportsEmpty'
                    | transloco
                }}
              </p>
            </section>
          </div>

          @for (tab of extraTabs; track tab.id) {
            <div [hlmTabsContent]="tab.id" class="pt-4">
              <ng-container
                *ngComponentOutlet="
                  tab.component;
                  inputs: {
                    personId: currentPerson.id,
                    organizationId: currentPerson.organizationId ?? null,
                    canEdit: canUpdate(),
                  }
                "
              />
            </div>
          }

          <div hlmTabsContent="settings" class="space-y-6 pt-4">
            @if (inviteNotice(); as notice) {
              <div
                class="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
              >
                {{
                  'persons.list.invite.sent'
                    | transloco: { email: notice.email }
                }}
                @if (notice.url) {
                  <span class="mt-1 block break-all text-emerald-700">
                    {{ 'organization.persons.inviteLink' | transloco }}:
                    {{ notice.url }}
                  </span>
                }
              </div>
            }

            <section class="space-y-3">
              <h3 class="text-base font-semibold text-foreground">
                {{ 'persons.list.details.sections.account' | transloco }}
              </h3>
              <dl
                class="overflow-hidden rounded-xl border border-border bg-background"
              >
                <div
                  class="grid gap-2 border-b border-border px-4 py-3 text-sm sm:grid-cols-2"
                >
                  <dt class="text-muted-foreground">
                    {{ 'persons.list.fields.accountStatus' | transloco }}
                  </dt>
                  <dd class="text-foreground sm:text-right">
                    {{
                      currentPerson.userId
                        ? ('persons.list.status.linked' | transloco)
                        : ('persons.list.status.invited' | transloco)
                    }}
                  </dd>
                </div>
                <div
                  class="grid gap-2 border-b border-border px-4 py-3 text-sm sm:grid-cols-2"
                >
                  <dt class="text-muted-foreground">
                    {{ 'contacts.form.fields.type' | transloco }}
                  </dt>
                  <dd class="text-foreground sm:text-right">
                    {{ 'contacts.types.' + currentPerson.type | transloco }}
                  </dd>
                </div>
                @if (currentPerson.company) {
                  <div class="grid gap-2 px-4 py-3 text-sm sm:grid-cols-2">
                    <dt class="text-muted-foreground">
                      {{ 'contacts.details.fields.company' | transloco }}
                    </dt>
                    <dd class="text-foreground sm:text-right">
                      {{ currentPerson.company }}
                    </dd>
                  </div>
                }
              </dl>

              @if (canSendInvite() && !currentPerson.userId) {
                <button
                  type="button"
                  class="inline-flex items-center rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary disabled:opacity-50"
                  [disabled]="sendingInvite()"
                  (click)="sendInvite()"
                >
                  {{ 'persons.list.actions.sendInvite' | transloco }}
                </button>
              }
            </section>

            @if (tags().length > 0) {
              <section class="space-y-3">
                <h3 class="text-base font-semibold text-foreground">
                  {{ 'contacts.details.tags' | transloco }}
                </h3>
                <div class="flex flex-wrap gap-2">
                  @for (tag of tags(); track tag) {
                    <span
                      class="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary"
                    >
                      {{ tag }}
                    </span>
                  }
                </div>
              </section>
            }

            <section class="space-y-3">
              <h3 class="text-base font-semibold text-foreground">
                {{ 'persons.list.details.sections.meta' | transloco }}
              </h3>
              <dl
                class="overflow-hidden rounded-xl border border-border bg-background"
              >
                <div
                  class="grid gap-2 border-b border-border px-4 py-3 text-sm sm:grid-cols-2"
                >
                  <dt class="text-muted-foreground">
                    {{ 'persons.profile.fields.personId' | transloco }}
                  </dt>
                  <dd class="font-mono text-foreground sm:text-right">
                    #{{ currentPerson.id }}
                  </dd>
                </div>
                <div
                  class="grid gap-2 border-b border-border px-4 py-3 text-sm sm:grid-cols-2"
                >
                  <dt class="text-muted-foreground">
                    {{ 'contacts.details.fields.createdAt' | transloco }}
                  </dt>
                  <dd class="text-foreground sm:text-right">
                    {{ currentPerson.createdAt | date: 'medium' }}
                  </dd>
                </div>
                @if (currentPerson.updatedAt) {
                  <div class="grid gap-2 px-4 py-3 text-sm sm:grid-cols-2">
                    <dt class="text-muted-foreground">
                      {{ 'contacts.details.fields.updatedAt' | transloco }}
                    </dt>
                    <dd class="text-foreground sm:text-right">
                      {{ currentPerson.updatedAt | date: 'medium' }}
                    </dd>
                  </div>
                }
              </dl>
            </section>
          </div>
        </hlm-tabs>
      </app-details-shell>
    } @else if (isLoading()) {
      <p class="p-6 text-sm text-muted-foreground">
        {{ 'persons.list.details.loading' | transloco }}
      </p>
    }
  `,
})
export class PersonDetailsComponent {
  readonly tableStateService = inject(TableStateService<IPerson>);
  readonly #flow = inject(PERSON_ADMIN_FLOW);
  readonly #scopedAccess = inject(SCOPED_ACCESS_GATE);
  /** Contributed by the application; see `PERSON_DETAIL_TABS`. */
  readonly extraTabs = inject(PERSON_DETAIL_TABS);
  readonly deletePerson = createDeletePersonAction();
  readonly resendInvite = createResendPersonInviteAction();

  readonly isLoading = this.tableStateService.isLoadingDetails;
  readonly person = this.tableStateService.selected;
  readonly sendingInvite = signal(false);
  readonly inviteNotice = signal<{ email: string; url: string | null } | null>(
    null,
  );

  readonly canSendInvite = computed(() =>
    this.#scopedAccess.can(
      PERSON_SCOPED_ENTITY,
      PERSON_SCOPED_ACTION.SEND_INVITE,
    ),
  );

  readonly canUpdate = computed(() =>
    this.#scopedAccess.can(PERSON_SCOPED_ENTITY, PERMISSION_ACTION.UPDATE),
  );

  readonly canDelete = computed(() =>
    this.#scopedAccess.can(PERSON_SCOPED_ENTITY, PERMISSION_ACTION.DELETE),
  );

  readonly fullName = computed(() => {
    const current = this.person();
    if (!current) return '';
    return `${current.firstName} ${current.lastName}`.trim();
  });

  readonly subtitle = computed(() => {
    const current = this.person();
    if (!current) return '';
    const parts = [current.title?.trim(), current.company?.trim()].filter(
      (part): part is string => !!part?.length,
    );
    return parts.join(' · ');
  });

  readonly tags = computed(() => {
    const raw = this.person()?.tags;
    if (!raw) return [];
    return raw
      .split(',')
      .map((tag: string) => tag.trim())
      .filter((tag: string) => tag.length > 0);
  });

  readonly visibleActions = computed((): Action[] => {
    const current = this.person();
    if (!current) return [];

    const actions: Action[] = [];

    if (this.canUpdate()) {
      actions.push({
        label: 'persons.list.actions.edit',
        icon: 'edit',
        function: () => this.edit(),
      });
    }

    if (this.canDelete()) {
      actions.push({
        label: this.#flow.deleteLabel?.() ?? 'common.actions.delete',
        icon: 'delete',
        linkType: 'danger',
        function: () => this.delete(),
      });
    }

    return actions;
  });

  constructor() {
    effect(() => {
      this.tableStateService.setDrawerTitle(this.fullName());
      void this.person()?.id;
      this.inviteNotice.set(null);
    });
  }

  displayValue(value: string | number | null | undefined): string {
    if (value === undefined || value === null || value === '') {
      return '—';
    }
    return String(value);
  }


  edit(): void {
    const id = this.tableStateService.selectedId();
    if (id == null) return;
    this.#flow.edit(String(id));
  }

  delete(): void {
    const id = this.tableStateService.selectedId();
    if (id == null) return;
    void this.deletePerson(String(id));
  }

  sendInvite(): void {
    const current = this.person();
    if (
      !current ||
      current.userId ||
      !this.canSendInvite() ||
      this.sendingInvite()
    ) {
      return;
    }

    this.sendingInvite.set(true);
    void this.resendInvite(current.id, current.email, current.organizationId)
      .then((inviteUrl) => {
        this.inviteNotice.set({ email: current.email, url: inviteUrl });
      })
      .finally(() => {
        this.sendingInvite.set(false);
      });
  }
}
